import { token, tokenWithOpacity } from '../../../../setup/theme/impl/getToken';
import { styled } from '../../../../styles';
import { dataTableClassnames } from '../const/classNames';
import { Box } from '../../../Box';

const { table, header, footer, slots, variant } = dataTableClassnames;

const is = <T extends string>(key: T): `&.${T}` => `&.${key}`;
const isNot = <T extends string>(...keys: T[]): `&:not(${string})` => `&:not(${keys.join(', ')})`;
const isHover = () => `&:hover` as const;
const isAfter = () => `&::after` as const;
const element = <T extends string>(key: T): `.${T}` => `.${key}`;
const isVariantPreview = is(variant('preview'));

export const DataTableStyles = styled(Box)`
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-rows: auto 1fr auto;
    container: root-component / inline-size;

    @container root-component (width > 1px) {
        ${element(table.noResults.root)} {
            width: 100cqw;
            display: flex;
        }
    }


    ${element(slots.children)} {
        width: 100%;
        height: 100%;
        overflow: hidden;
    }

    ${element(header.root)}:not(:empty) {
        padding: ${({ theme }) => theme.spacing(5)};
        padding-block-start: 0;
        position: relative;
    }

    ${element(footer.root)} {
        border-top: 1px solid ${({ theme }) => theme.tokens.colorSurfaceVariant};
        padding-block-start: ${({ theme }) => theme.spacing(5)};
        display: flex;
        justify-content: flex-end;
    }

    ${is('has-pagination')} ${element(table.row.body)}:last-child {
        border-bottom: none;
    }

    // Table
    ${element(table.root)} {
        position: relative;
        width: 100%;
        height: 100%;
        display: grid;
        grid-template-rows: auto 1fr;
        overflow: auto;
        scroll-behavior: smooth;
        scrollbar-width: thin;
        scrollbar-gutter: stable;
        scrollbar-color: ${({ theme }) => `${theme.tokens.colorSurfaceVariant} transparent`};

        // Table empty / loading
        ${is(table.state.empty)}, ${is(table.state.loading)} {
            overflow: hidden;
        }

        // Table loading
        ${is(table.state.loading)} ${element(table.body)}  {
            min-height: 128px;
        }
        
        // Table Thead
        ${element(table.head)} {
            border-top: 1px solid ${({ theme }) => theme.tokens.colorSurfaceVariant};
            background-color: ${({ theme }) => theme.tokens.colorSurface};
            position: sticky;
            top: 0;
            z-index: 2;
        }

        // Table tr
        ${element(table.row.root)} {
            background-color: ${({ theme }) => theme.tokens.colorSurface};
            color: ${({ theme }) => theme.tokens.colorOnSurface};
            display: flex;
            align-items: stretch;
            width: 100%;
            min-height: 48px;
            border-bottom: 1px solid ${({ theme }) => theme.tokens.colorSurfaceVariant};

            // nested row is followed by the top-level row
            ${is(table.row.depth(1))} + ${element(table.row.depth(0))} {
                border-top: 1px solid ${({ theme }) => theme.tokens.colorSurfaceVariant};;
            }

            // only nested rows - is not a top-level row or a heading row 
            ${isNot(element(table.row.depth(0)), element(table.row.head))} {
                animation: sub-row-fade-in .2s ease-in-out forwards;
                border-bottom-color: transparent;
            }

            // td in body section
            ${is(table.row.body)} {

                // is any row followed by the selected row
                &:has(+ ${element(table.row.state.selected)}) {
                    border-bottom-color: ${({ theme }) => theme.tokens.colorInversePrimary};
                }
                
                // selected
                ${is(table.row.state.selected)} {
                    background-color: ${({ theme }) => theme.tokens.colorSecondaryContainer};
                    border-bottom-color: ${({ theme }) => theme.tokens.colorInversePrimary};

                    ${element(table.cell.menu)} {
                        background: ${({ theme }) => `linear-gradient(90deg, ${tokenWithOpacity('colorSecondaryContainer', '0%')} 0%, ${theme.tokens.colorSecondaryContainer} 30%, ${theme.tokens.colorSecondaryContainer} 100%)`};
                    }
                }
                
                // this row:hover
                ${isHover()} {
                    background-color: ${token('paletteNeutral95')}; // @migrate tokens v5 #efefef;

                    // menu cell
                    ${element(table.cell.menu)} {
                        background: linear-gradient(90deg, ${tokenWithOpacity('paletteNeutral95', '0%')} 0%, ${token('paletteNeutral95')} 30%, ${token('paletteNeutral95')} 100%);
                    }

                    // selected row
                    ${is(table.row.state.selected)} {
                        background-color: ${({ theme }) => theme.tokens.colorSecondaryContainer}; // @migrate tokens v5 #b9d9f1;
                        filter: brightness(95%);
                        
                        // menu cell
                        ${element(table.cell.menu)} {
                            // @migrate tokens v5; background: linear-gradient(90deg, #b9d9f100 0%, #b9d9f1 30%, #b9d9f1 100%);
                            background: ${({ theme }) => `linear-gradient(90deg, ${tokenWithOpacity('colorSecondaryContainer', '0%')} 0%, ${theme.tokens.colorSecondaryContainer} 30%, ${theme.tokens.colorSecondaryContainer} 100%)`};
                        }
                    }
                }
            }

            // tr > td || th > td
            ${element(table.cell.root)} {
                overflow: hidden;
                position: relative;
                display: flex;
                align-items: center;
                
                [data-content-parent] {
                    overflow: hidden;
                    display: block;
                    text-overflow: ellipsis;
                }

                // is th > td
                ${is(table.cell.head)} {
                    overflow: visible;

                    ${isHover()} {
                        ${element(table.cell.resizer.root)} {
                            opacity: 0.2;
                        }
                    }

                    // column resizer
                    ${element(table.cell.resizer.root)} {
                        transition: opacity 0.2s ease-in-out;
                        position: absolute;
                        top: 0;
                        height: 100%;
                        width: 15px;
                        cursor: col-resize;
                        user-select: none;
                        touch-action: none;
                        right: 0;
                        opacity: 0;

                        ${isAfter()} {
                            content: '';
                            position: absolute;
                            right: 6px;
                            top: 0;
                            display: block;
                            width: 3px;
                            background: rgba(0, 0, 0, 0.3);
                        }

                        ${isHover()} {
                            opacity: 1;
                        }

                        // resizing
                        ${is(table.cell.resizer.state.resizing)} {
                            opacity: 1;
                            pointer-events: none;

                            ${isAfter()}{
                                background: rgba(0, 0, 0, 0.5);
                            }
                        }
                    }
                }

                // all direct children of td but ignore resizer
                & > *:not(${element(table.cell.resizer.root)}) {
                    padding-inline: ${({ theme }) => theme.spacing(5)};
                }
                
                // select-row and menu cells
                ${is(table.cell.selectRow)} > *, ${is(table.cell.menu)} > * {
                    padding-inline: ${({ theme }) => theme.spacing(2)};
                }

                // select-row cell
                ${is(table.cell.selectRow)} {
                    .MuiFormControlLabel-root {
                        margin: 0;
                    }
                }

                // menu cell
                ${is(table.cell.menu)} {
                    position: sticky;
                    right: 0;
                    background: ${({ theme }) => `linear-gradient(90deg, ${tokenWithOpacity('colorSurface', '0%')} 0%, ${theme.tokens.colorSurface} 30%, ${theme.tokens.colorSurface} 100%)`};
                }

                // cell content (td > cellContent)
                ${element(table.cell.content)} {
                    display: flex;
                    gap: ${({ theme }) => theme.spacing(2)};
                    align-items: center;
                    width: 100%;
                }
            }
        }
    }
    
    /**
      * VARIANT: PREVIEW
     */
    ${isVariantPreview} {
        // Table
        ${element(table.root)} {
            ${element(table.head)} {
                border-top: none;
            }
            ${element(table.row.root)} {
                ${is(table.row.head)} {
                    background-color: #F4F3F7; // missing color/surface-container-low
                }
                ${is(table.row.body)} {
                    min-height: 0;
                }
                
                ${element(table.cell.root)} {
                    & + ${element(table.cell.root)} {
                        border-left: 1px solid ${({ theme }) => theme.tokens.colorSurfaceVariant};
                    }
                    ${is(table.cell.head)} {
                        
                        .MuiTypography-root {
                            font-weight: ${({ theme }) => theme.font.weight.mediumBold};
                        }
                        [data-content-parent] {
                            white-space: break-spaces;
                            overflow: initial; 
                            text-overflow: initial;
                        }
                    }
                    ${is(table.cell.body)} {
                        padding-block: ${({ theme }) => theme.spacing(3)};
                    }
                }
                
            }
        }
    }
    
    // animation for nested rows
    @keyframes sub-row-fade-in {
        0% {
            transform: translateY(-3px);
            opacity: 0;
        }
        100% {
            transform: translateY(0);
            opacity: 1;
        }
    }

`;
